<template>
  <div ref="infoWindowContent" style="display: none;">
    <div class="info-window-content" >
      <div class="columns is-multiline">
      <!-- Header with image and heart icon -->
      <div class="column image-header">
        <div class="image-container">
          <figure class="image" v-if="hotelImage">
            <img :src="hotelImage" :alt="hotel.hotelName" class="hotel-image">
          </figure>
          <div v-else class="image-placeholder">
            <span class="icon is-large has-text-grey-light">
              <i class="fas fa-building fa-2x"></i>
            </span>
          </div>
          
        </div>
        
        <!-- Heart/favorite icon -->
         <!-- Placeholder for favorite button
        <button class="favorite-btn">
          <i class="far fa-heart"></i>
        </button>
      -->
      </div>
      
      <!-- Hotel details section -->
      <div class="column hotel-details">
        <!-- Hotel name and distance -->
        <div class="hotel-header content-section">
          <h3 class="hotel-name">{{ hotel.hotelName }}</h3>

          <!-- Location info -->
          <div class="location-info">
            <i class="fas fa-map-marker-alt location-icon"></i>
            <span class="distance-text">
              {{ distanceText }}
            </span>
          </div>
        </div>
        


        <!-- Two-column compact layout -->
        <div class="details-grid">
          <!-- Left column: Amenities and Reserve info -->
          <div class="left-column">
            <!-- Amenities (compact version) -->
            <div class="amenities-compact" v-if="availableAmenities.length > 0">
              <div
                v-for="(amenity, index) in availableAmenities"
                :key="index"
                class="amenity-badge"
              >
                <i :class="amenity.icon" class="amenity-icon"></i>
                <span>{{ amenity.label }}</span>
              </div>
            </div>

            <!-- Reserve info -->
             
            <!--
            <div class="reserve-compact">
              <p class="reserve-text">
                <i class="fas fa-check-circle"></i>
                Reserve now, pay later
              </p>
            </div>
          -->

          <div class="view-button has-text-link is-clickable is-size-7 has-text-weight-bold has-background-primary p-2 mb-4 has-text-white" style="border-radius: 10px;width:150px;max-width: fit-content;"> View hotel <i class="fas fa-arrow-right" aria-hidden="true"></i>  </div>
          
          </div>

          <!-- Right column: Pricing -->
          <div class="right-column">
            <div class="pricing-section">
              <div class="price-container">
                
                <div class="price-row">
                  <span class="price-label">approx</span>
                  <span class="old-price" v-if="pricing.originalRate">
                    ${{ pricing.originalRate }}
                  </span>
                  <span class="price-amount">${{ pricing.dailyRate }}</span>
                  <span class="price-period">nightly</span>
                </div>
              <div class="total-price">
                ${{ pricing.total }} total
              </div>
              </div>

              <p class="tax-info">
                includes taxes & fees
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
/* global google */
import { mapState } from 'vuex'
import pathMixin from '@/mixins/pathMixin'
import meetingsMixin from '@/mixins/meetingsMixin'

export default {
  name: 'HotelMapInfoWindow',
  mixins: [pathMixin, meetingsMixin],
  props: {
    hotel: {
      type: Object,
      required: true
    },
    map: {
      type: Object,
      required: true
    },
    searchLocationName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      infoWindow: null,
      currentImageIndex: 0,
      hotelNameClickListener: null,
      hotelImageClickListener: null
    };
  },
  computed: {
    ...mapState({
      amenityOptions: (state) => state.hotelSearch.filters.amenities.options,
      searchForm: (state) => state.hotelSearch.searchForm,
    }),

    availableAmenities() {
      return this.amenityOptions.filter((amenityOption) => {
        return this.hotel.description?.amenities?.includes(amenityOption.value)
      })
    },
    cacheKey() {
      // Get cache key from URL parameter
      return this.$route.query.key || null
    },

    distanceText() {
      return `${this.hotel.coordinates.distanceFromSearchedLocation.toFixed(2)} mi away`
    },

    pricing() {
      const room = this.hotel.rooms && this.hotel.rooms[0];
      if (!room) return { dailyRate: 0, total: 0 };
      
      const pricing = room.requestedCurrencyPricing || {};
      return {
        dailyRate: pricing.dailyRate || 0,
        total: pricing.total || 0,
        originalRate: this.getOriginalRate(room)
      };
    },
    
    hotelImage() {
      const images = this.getHotelImages();
      return images.length > 0 ? images[this.currentImageIndex] : null;
    },
    
    hasMultipleImages() {
      return this.getHotelImages().length > 1;
    },
    

    
    rating() {
      // Generate a realistic rating based on hotel data
      const baseRating = 3.5 + (Math.random() * 1.5); // Random rating between 3.5-5.0
      const score = Math.round(baseRating * 10) / 10;
      const reviewCount = Math.floor(Math.random() * 1000) + 100; // 100-1100 reviews
      
      let text = 'Good';
      if (score >= 4.5) text = 'Excellent';
      else if (score >= 4.0) text = 'Very Good';
      else if (score >= 3.5) text = 'Good';
      else text = 'Fair';
      
      return {
        score: score.toFixed(1),
        text,
        reviewCount
      };
    }
  },
  mounted() {
    this.createInfoWindow();
  },
  beforeDestroy() {
    this.cleanupClickListeners();
    this.closeInfoWindow();
  },
  methods: {
    getHotelImages() {
      if (this.hotel.description && 
          this.hotel.description.imagesAndDescriptions && 
          this.hotel.description.imagesAndDescriptions.hotel &&
          this.hotel.description.imagesAndDescriptions.hotel.images) {
        return this.hotel.description.imagesAndDescriptions.hotel.images.map(img => img.url);
      }
      return [];
    },
    
    getOriginalRate(room) {
      // Check if there's a higher original rate to show as crossed out
      if (room.originalPricing && room.requestedCurrencyPricing) {
        const originalDaily = Math.round(room.originalPricing.AmountAfterTax / this.hotel.tripLength);
        const currentDaily = room.requestedCurrencyPricing.dailyRate;
        return originalDaily > currentDaily ? originalDaily : null;
      }
      return null;
    },
    
    createInfoWindow() {
      if (!window.google) return;

      this.infoWindow = new google.maps.InfoWindow({
        content: this.$refs.infoWindowContent.innerHTML,
        maxWidth: 700,
        pixelOffset: new google.maps.Size(0, -10),
        clickable: true
      });
    },
    
    openInfoWindow(marker) {
      if (this.infoWindow) {
        // Update content with current hotel data
        this.infoWindow.setContent(this.$refs.infoWindowContent.innerHTML);
        this.infoWindow.open(this.map, marker);

        // Add event listeners for hotel title and image clicks after InfoWindow is opened
        this.$nextTick(() => {
          // Clean up previous listeners if they exist
          this.cleanupClickListeners();

          // Use a more specific selector and add retry logic
          setTimeout(() => {
            // Add click listener to hotel name
            const hotelNameElement = document.querySelector('.gm-style-iw-c .hotel-name') ||
                                   document.querySelector('.gm-style-iw .hotel-name');
            if (hotelNameElement) {
              this.hotelNameClickListener = this.gotoHotelDetails.bind(this);
              hotelNameElement.addEventListener('click', this.hotelNameClickListener);
              hotelNameElement.style.cursor = 'pointer';
            }

            // Add click listener to hotel image
            const hotelImageElement = document.querySelector('.gm-style-iw-c .hotel-image') ||
                                    document.querySelector('.gm-style-iw .hotel-image') ||
                                    document.querySelector('.gm-style-iw-c .image-container') ||
                                    document.querySelector('.gm-style-iw .image-container');
            if (hotelImageElement) {
              this.hotelImageClickListener = this.gotoHotelDetails.bind(this);
              hotelImageElement.addEventListener('click', this.hotelImageClickListener);
              hotelImageElement.style.cursor = 'pointer';
            }

            const viewButtonElement = document.querySelector('.gm-style-iw-c .view-button') ||
                                   document.querySelector('.gm-style-iw .view-button');
            if (viewButtonElement) {
              this.viewButtonClickListener = this.gotoHotelDetails.bind(this);
              viewButtonElement.addEventListener('click', this.viewButtonClickListener);
              viewButtonElement.style.cursor = 'pointer';
            }


          }, 100); // Small delay to ensure InfoWindow DOM is ready
        });

        // Emit event to parent to handle closing other info windows
        this.$emit('info-window-opened', this.infoWindow);

        // Center map on marker
        this.map.panTo(marker.getPosition());
      }
    },
    
    closeInfoWindow() {
      if (this.infoWindow) {
        this.cleanupClickListeners();
        this.infoWindow.close();
      }
    },

    cleanupClickListeners() {
      // Clean up hotel name click listener
      if (this.hotelNameClickListener) {
        const hotelNameElement = document.querySelector('.gm-style-iw-c .hotel-name') ||
                               document.querySelector('.gm-style-iw .hotel-name');
        if (hotelNameElement) {
          hotelNameElement.removeEventListener('click', this.hotelNameClickListener);
        }
        this.hotelNameClickListener = null;
      }

      // Clean up hotel image click listener
      if (this.hotelImageClickListener) {
        const hotelImageElement = document.querySelector('.gm-style-iw-c .hotel-image') ||
                                document.querySelector('.gm-style-iw .hotel-image') ||
                                document.querySelector('.gm-style-iw-c .image-container') ||
                                document.querySelector('.gm-style-iw .image-container');
        if (hotelImageElement) {
          hotelImageElement.removeEventListener('click', this.hotelImageClickListener);
        }
        this.hotelImageClickListener = null;
      }
    },
    
    getInfoWindow() {
      return this.infoWindow;
    },

    gotoHotelDetails() {
      // Save hotel to store before navigation (similar to Result.vue)
      this.$store.dispatch("hotelSearch/saveHotel", this.hotel)

      if (this.pathMixin_isMeeting) {
        this.$nav.pushRouteTo({
          name: "MeetingHotelDetail",
          params: { hotelCode: this.hotel.hotelCode },
          query: { key: this.cacheKey }
        })
      } else {
        this.$nav.pushRouteTo({
          name: "hotel-detail",
          params: { hotelCode: this.hotel.hotelCode, key: this.cacheKey },
          query: { key: this.cacheKey }
        })
      }
    }
  }
};
</script>

<style scoped>
.info-window-content {
  max-width: 700px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  animation: slideInUp 0.3s ease-out;
  padding: 5px;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Image header section */
.image-header {
  position: relative;
  height: 180px;
  overflow: hidden;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.hotel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  cursor: pointer;
}

.image-header:hover .hotel-image {
  transform: scale(1.05);
}

.image-container {
  cursor: pointer;
}

/* Image overlay for better text readability */
.image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.3));
  height: 40px;
  pointer-events: none;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.favorite-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 2;
}

.favorite-btn:hover {
  background: white;
  color: #e74c3c;
}

.favorite-btn i {
  font-size: 16px;
}

/* Hotel details section */
.hotel-details {
  /* padding: 16px; */
  /* height: 180px; */ /* Match image height for compact design */
  display: flex;
  flex-direction: column;
}

.content-section {
  
}

.content-section:last-child {
  margin-bottom: 0;
}

/* Two-column grid layout */
.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  flex: 1;
  align-items: start;
}

.left-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.right-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.hotel-name {
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 8px 0;
  color: #1a1a1a;
  cursor: pointer;
  transition: color 0.2s ease;
}

.hotel-name:hover {
  color: #2c5aa0;
}

/* Location info */
.location-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  color: #666;
  font-size: 0.875rem;
}

.location-icon {
  color: #a4393a;
  font-size: 12px;
}

.distance-text {
  color: #666;
}

/* Compact Amenities */
.amenities-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.amenity-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 10px;
  font-size: 11px;
  color: #333;
  transition: all 0.2s ease;
  white-space: nowrap;
  font-weight: 400;
}

.amenity-icon {
  font-size: 11px;
  color: #333;
}

/* Compact Reserve section */
.reserve-compact {
  margin-top: auto; /* Push to bottom of left column */
}

.reserve-text {
  font-size: 0.75rem;
  margin: 0;
  color: #28a745;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.view-details-text {
  font-size: 0.8rem;
  cursor: pointer;
  margin: 0;
  color: #0963ea;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.view-details-text:hover {
  text-decoration: underline
}

.reserve-text i {
  font-size: 10px;
}

/* Compact Pricing section */
.pricing-section {
  display: flex;
  flex-direction: column;
  /* justify-content: space-between; */
  height: 100%;
  text-align: right;
}

.price-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.price-label {
  font-size: 0.75rem;
  color: #666;
}

.price-period {
  font-size: 0.75rem;
  color: #666;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.price-amount {
  font-size: 1.05rem;
  font-weight: 700;
  color: #3e8ed0;
  line-height: 1;
}

.old-price {
  text-decoration: line-through;
  font-size: 0.875rem;
  color: #999;
}


.total-price {
  font: 1em;
  font-weight: 500;
  color: #10171e;
  margin-top: 2px;
  margin-bottom: 2px;
}

.tax-info {
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  font-size: 0.65rem;
  color: #28a745;
  /*margin-top: auto;*/ /* Push to bottom */
}

.tax-info i {
  font-size: 8px;
}

/* Responsive adjustments */
@media screen and (max-width: 800px) {

}

@media screen and (max-width: 600px) {

  .hotel-name {
    font-size: 1.25rem;
  }

  .price-amount {
    font-size: 1.125rem;
  }

  .image-header {
    height: 160px;
  }

  .hotel-details {
    /* height: 160px; */ /* Match reduced image height */
  }

  .details-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .right-column {
    text-align: left;
  }

  .price-container {
    align-items: flex-start;
  }

  .tax-info {
    justify-content: flex-start;
  }
}

@media screen and (max-width: 450px) {


  .amenity-badge {
    font-size: 0.65rem;
    padding: 3px 6px;
  }

  .price-amount {
    font-size: 1rem;
  }
}
</style>